import { useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

export const ContactDialog = ({ open, onClose }) => {
  const { t, locale } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, locale }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(t.form.success);
      setForm({ name: "", email: "", message: "" });
      onClose();
    } catch (err) {
      toast.error(t.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-stretch justify-end bg-[#0d1620]/60 backdrop-blur-sm"
      data-testid="contact-dialog-overlay"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xl h-full bg-[var(--lab-cream)] shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="contact-dialog"
      >
        <div className="grain-overlay opacity-30" />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          data-testid="contact-close-btn"
          className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full border border-[var(--lab-line)] flex items-center justify-center bg-[var(--lab-cream)] hover:bg-[var(--lab-ink)] hover:text-[var(--lab-cream)] transition-colors cursor-pointer"
          aria-label={t.form.close}
        >
          <X size={18} />
        </button>

        <div className="px-8 md:px-12 pt-16 pb-12 relative">
          <div className="font-mono-label text-[var(--lab-orange)] mb-6">
            LAB · 2513
          </div>
          <h2 className="font-display text-5xl md:text-6xl text-[var(--lab-ink)] leading-[0.95] mb-2">
            {t.form.title.split(" ").slice(0, -1).join(" ")}
          </h2>
          <h2 className="font-serif-italic text-5xl md:text-6xl text-[var(--lab-ink)] leading-[0.95] mb-8">
            {t.form.title.split(" ").slice(-1)}
          </h2>
          <p className="text-[var(--lab-muted)] text-base mb-12 max-w-md">
            {t.form.subtitle}
          </p>

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
            <div>
              <label className="font-mono-label text-[var(--lab-muted)] block mb-1">
                {t.form.name}
              </label>
              <input
                required
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.form.namePh}
                className="lab-input"
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <label className="font-mono-label text-[var(--lab-muted)] block mb-1">
                {t.form.email}
              </label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.form.emailPh}
                className="lab-input"
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <label className="font-mono-label text-[var(--lab-muted)] block mb-1">
                {t.form.message}
              </label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t.form.messagePh}
                className="lab-input resize-none"
                data-testid="contact-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-pill bg-[var(--lab-ink)] text-[var(--lab-cream)] hover:bg-[var(--lab-orange)] disabled:opacity-50 disabled:cursor-not-allowed group"
              data-testid="contact-submit-btn"
            >
              {submitting ? t.form.submitting : t.form.submit}
              <ArrowRight size={16} className="arrow-anim" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
